import { motionOk } from "../kit";

/**
 * Swipe between owners on the paper doll: a sideways drag on the Loadout stage moves to the previous / next owner in
 * the owner strip's order. The gesture locks to an axis after a few px — vertical hands the finger back to the page
 * (the stage is `touch-action: pan-y`), horizontal follows the finger — and a tap that never travelled stays a tap
 * (the sockets' own clicks). DOM UI only (the "views don't read pointers" rule is about the 3D render views).
 * The decisions (`swipeAxis`, `swipeStep`, `revealScroll`) are pure so they can be tested headless.
 */

/** Travel (px) before the gesture picks an axis; under it, it is still a tap. */
export const SWIPE_LOCK_PX = 10;
/** A drag this far sideways (px) moves to the next owner… */
export const SWIPE_MIN_PX = 40;
/** …or a quick flick: at least this far, released within FLICK_MAX_MS of touching down. */
export const FLICK_MIN_PX = 16;
export const FLICK_MAX_MS = 250;

export type SwipeAxis = "x" | "y" | null;

/** Which way the finger is going once it has travelled SWIPE_LOCK_PX (null before; a tie is vertical: scrolling wins). */
export function swipeAxis(dx: number, dy: number): SwipeAxis {
  if (Math.hypot(dx, dy) < SWIPE_LOCK_PX) return null;
  return Math.abs(dx) > Math.abs(dy) ? "x" : "y";
}

/** On release: +1 (finger went left: the next owner), −1 (went right: the previous one) or 0 (not a swipe). */
export function swipeStep(dx: number, dy: number, ms: number): 1 | -1 | 0 {
  const ax = Math.abs(dx);
  if (ax <= Math.abs(dy)) return 0;
  const far = ax >= SWIPE_MIN_PX;
  const flick = ax >= FLICK_MIN_PX && ms <= FLICK_MAX_MS;
  if (!far && !flick) return 0;
  return dx < 0 ? 1 : -1;
}

/**
 * The scroll position that shows [start, end) (content px) in a view `width` wide scrolled to `scroll`, moving as
 * little as possible; `padStart` / `padEnd` keep it clear of the edges (the strip's right edge fades out).
 */
export function revealScroll(scroll: number, width: number, start: number, end: number, padStart: number, padEnd: number): number {
  if (start - padStart < scroll) return Math.max(0, start - padStart);
  if (end + padEnd > scroll + width) return Math.max(0, end + padEnd - width);
  return scroll;
}

/** The stage follows the finger at this fraction of its travel (a drag, not a scroll)… */
const FOLLOW = 0.6;
/** …and only this much at either end of the strip (a rubber band: nothing that way). */
const EDGE_FOLLOW = 0.2;
/** While following, the stage fades by 1 − |x| / this, never under FOLLOW_MIN_OPACITY. */
const FOLLOW_FADE_PX = 240;
const FOLLOW_MIN_OPACITY = 0.35;
/** The next owner's stage slides in from this far on the swipe's side, over SLIDE_MS. */
const SLIDE_IN_PX = 48;
const SLIDE_MS = 200;
/** A drag short of a swipe springs back over this long. */
const SNAP_MS = 160;
/** The click that follows a sideways drag's release is swallowed for this long (it is not a tap on a socket). */
const CLICK_GUARD_MS = 400;

export interface OwnerSwipeHooks {
  /** Whether a swipe may start (the Loadout tab is showing and no panel is open over it). */
  canStart(): boolean;
  /** Whether there is an owner that way (at the ends the stage only rubber-bands). */
  hasNext(step: 1 | -1): boolean;
  /** Select the owner one step along and re-render the page (the new stage replaces the old one). */
  go(step: 1 | -1): void;
}

const canAnimate = (e: Element): boolean => motionOk() && typeof (e as HTMLElement).animate === "function";

/**
 * Wires the swipe on `host` (the page body, which outlives re-renders) for pointers that start inside `stageSel`.
 * Motion is transform / opacity only; with reduced motion the stage does not follow and the switch is instant.
 */
export function ownerSwipe(host: HTMLElement, stageSel: string, hooks: OwnerSwipeHooks): void {
  let id: number | null = null;
  let x0 = 0, y0 = 0, t0 = 0, dx = 0, dy = 0;
  let axis: SwipeAxis = null;
  let stage: HTMLElement | null = null;
  let guardUntil = 0;

  const reset = () => { id = null; axis = null; stage = null; dx = 0; dy = 0; };
  const clear = (el: HTMLElement) => { el.style.transform = ""; el.style.opacity = ""; el.style.transition = ""; };

  host.addEventListener("pointerdown", (e: PointerEvent) => {
    if (id !== null || e.isPrimary === false || (e.pointerType === "mouse" && e.button !== 0)) return;
    const st = (e.target as Element | null)?.closest?.<HTMLElement>(stageSel) ?? null;
    if (st === null || !hooks.canStart()) return;
    id = e.pointerId; x0 = e.clientX; y0 = e.clientY; t0 = performance.now(); stage = st;
  });
  host.addEventListener("pointermove", (e: PointerEvent) => {
    if (e.pointerId !== id || stage === null) return;
    dx = e.clientX - x0; dy = e.clientY - y0;
    if (axis === null) {
      axis = swipeAxis(dx, dy);
      if (axis === "y") { reset(); return; }
      if (axis === null) return;
    }
    if (!canAnimate(stage)) return;
    const edge = !hooks.hasNext(dx < 0 ? 1 : -1);
    const x = dx * (edge ? EDGE_FOLLOW : FOLLOW);
    stage.style.transition = "none";
    stage.style.transform = `translateX(${x}px)`;
    stage.style.opacity = edge ? "" : String(Math.max(FOLLOW_MIN_OPACITY, 1 - Math.abs(x) / FOLLOW_FADE_PX));
  });
  const end = (e: PointerEvent, cancelled: boolean) => {
    if (e.pointerId !== id || stage === null) return;
    const was = stage, sideways = axis === "x";
    if (!cancelled) { dx = e.clientX - x0; dy = e.clientY - y0; }
    const step = cancelled || !sideways ? 0 : swipeStep(dx, dy, performance.now() - t0);
    reset();
    if (!sideways) return;
    guardUntil = performance.now() + CLICK_GUARD_MS;
    if (step !== 0 && hooks.hasNext(step)) {
      clear(was);
      hooks.go(step);
      const next = host.querySelector<HTMLElement>(stageSel);
      if (next !== null && canAnimate(next)) {
        next.animate([{ transform: `translateX(${step * SLIDE_IN_PX}px)`, opacity: 0 }, { transform: "translateX(0)", opacity: 1 }], { duration: SLIDE_MS, easing: "cubic-bezier(.2,.8,.3,1)" });
      }
      return;
    }
    if (!canAnimate(was)) { clear(was); return; }
    was.style.transition = `transform ${SNAP_MS}ms ease-out, opacity ${SNAP_MS}ms ease-out`;
    was.style.transform = "";
    was.style.opacity = "";
    window.setTimeout(() => { if (was.style.transform === "") was.style.transition = ""; }, SNAP_MS);
  };
  host.addEventListener("pointerup", (e: PointerEvent) => end(e, false));
  host.addEventListener("pointercancel", (e: PointerEvent) => end(e, true));
  // A sideways drag ends with a click on whatever is under the finger: it was a swipe, not a tap on a socket.
  host.addEventListener("click", (e) => { if (performance.now() < guardUntil) { guardUntil = 0; e.stopPropagation(); e.preventDefault(); } }, true);
}
